import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "wouter";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  ShoppingCart,
  Trash2,
  Printer,
  Copy,
  Check,
  Loader2,
  ChevronLeft,
} from "lucide-react";
import {
  parseAmount,
  formatAmount,
  normalizeUnit,
  combineConvertibleAmounts,
  INGREDIENT_CATEGORY_LABELS,
  INGREDIENT_CATEGORY_ORDER,
  type IngredientCategory,
} from "@workspace/recipe-utils";

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

interface ShoppingItem {
  id: number;
  recipeId: number | null;
  sourceIngredientId: number | null;
  recipeTitle: string | null;
  multiplier: string;
  item: string;
  amount: string | null;
  unit: string | null;
  prepNotes: string | null;
  category: string;
  checked: boolean;
  addedAt: string;
}

// One displayed line: the canonical item text + the underlying rows so
// individual sub-quantities can be checked off and we know which rows
// to delete on remove.
interface CombinedLine {
  key: string;
  item: string;
  // True when every underlying row is checked.
  allChecked: boolean;
  // [{ amount, unit, recipeTitle, rowId }] — sub-line breakdown shown
  // when the same item came in with non-combinable units.
  parts: Array<{
    rowId: number;
    amount: string | null;
    unit: string | null;
    recipeTitle: string | null;
    multiplier: string;
    prepNotes: string | null;
    checked: boolean;
  }>;
  // Displayed combined amount when units could be merged. Null when
  // the parts couldn't be combined (we then show each part separately).
  combinedAmount: string | null;
  combinedUnit: string | null;
  // All underlying row ids — used by "remove this line" actions.
  rowIds: number[];
}

function normalizeItem(item: string): string {
  return item
    .trim()
    .toLowerCase()
    .replace(/[,.]/g, "")
    .replace(/\s+/g, " ");
}

/**
 * Combine duplicate ingredients across recipes.
 *
 * Strategy:
 *   1. Group by normalized item name.
 *   2. Within each item group, try to merge ALL parts using
 *      `combineConvertibleAmounts` — that handles cross-unit math
 *      (tsp + tbsp -> tbsp, oz + lb -> lb, g + kg -> kg).
 *   3. If that fails (unknown unit, mixed families, or unparseable
 *      amounts), fall back to per-unit grouping: parts that share an
 *      exact normalized unit AND have parseable amounts merge by
 *      simple addition; everything else renders as its own sub-line
 *      under the item header.
 */
function combineItems(items: ShoppingItem[]): CombinedLine[] {
  const byItem = new Map<string, ShoppingItem[]>();
  for (const it of items) {
    const k = normalizeItem(it.item);
    if (!byItem.has(k)) byItem.set(k, []);
    byItem.get(k)!.push(it);
  }

  const result: CombinedLine[] = [];
  for (const [k, group] of byItem) {
    const allChecked = group.every((g) => g.checked);
    const rowIds = group.map((g) => g.id);

    // ── Path 1: try a single cross-unit conversion across all parts ──
    const numericParts: { amount: number; unit: string }[] = [];
    let allConvertible = true;
    for (const it of group) {
      const v = it.amount ? parseAmount(it.amount) : null;
      const u = normalizeUnit(it.unit);
      if (v === null || !u) {
        allConvertible = false;
        break;
      }
      numericParts.push({ amount: v, unit: u });
    }

    const combined = allConvertible
      ? combineConvertibleAmounts(numericParts)
      : null;

    if (combined && group.length >= 1) {
      result.push({
        key: k,
        item: group[0].item,
        allChecked,
        parts: [
          {
            rowId: group[0].id,
            amount: combined.amount,
            unit: combined.unit,
            recipeTitle: null,
            multiplier: "1",
            prepNotes: null,
            checked: allChecked,
          },
        ],
        combinedAmount: combined.amount,
        combinedUnit: combined.unit,
        rowIds,
      });
      continue;
    }

    // ── Path 2: fall back to same-unit grouping with sub-lines ──
    const byUnit = new Map<string, ShoppingItem[]>();
    for (const it of group) {
      const u = normalizeUnit(it.unit);
      if (!byUnit.has(u)) byUnit.set(u, []);
      byUnit.get(u)!.push(it);
    }

    const parts: CombinedLine["parts"] = [];
    let primaryAmount: string | null = null;
    let primaryUnit: string | null = null;
    let primaryAssigned = false;

    for (const sub of byUnit.values()) {
      let sum = 0;
      let allParseable = true;
      for (const s of sub) {
        const v = s.amount ? parseAmount(s.amount) : null;
        if (v === null) {
          allParseable = false;
          break;
        }
        sum += v;
      }

      if (allParseable && sub.length > 0 && sum > 0) {
        const mergedAmount = formatAmount(sum);
        const mergedUnit = sub[0].unit ?? null;
        const mergedRecipes = Array.from(
          new Set(sub.map((s) => s.recipeTitle).filter(Boolean) as string[]),
        ).join(", ");
        const subAllChecked = sub.every((s) => s.checked);

        parts.push({
          rowId: sub[0].id,
          amount: mergedAmount,
          unit: mergedUnit,
          recipeTitle: mergedRecipes || null,
          multiplier: "1",
          prepNotes: null,
          checked: subAllChecked,
        });

        if (!primaryAssigned) {
          primaryAmount = mergedAmount;
          primaryUnit = mergedUnit;
          primaryAssigned = true;
        }
      } else {
        for (const s of sub) {
          parts.push({
            rowId: s.id,
            amount: s.amount,
            unit: s.unit,
            recipeTitle: s.recipeTitle,
            multiplier: s.multiplier,
            prepNotes: s.prepNotes,
            checked: s.checked,
          });
        }
      }
    }

    const onlyOneMerged =
      byUnit.size === 1 && parts.length === 1 && primaryAmount !== null;

    result.push({
      key: k,
      item: group[0].item,
      allChecked,
      parts,
      combinedAmount: onlyOneMerged ? primaryAmount : null,
      combinedUnit: onlyOneMerged ? primaryUnit : null,
      rowIds,
    });
  }

  return result;
}

export default function ShoppingListPage() {
  const { toast } = useToast();
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${getBaseUrl()}/api/shopping-list`);
      if (!res.ok) throw new Error("Failed to load");
      const json = (await res.json()) as { items: ShoppingItem[] };
      setItems(json.items ?? []);
    } catch {
      toast({
        title: "Couldn't load shopping list",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setRowChecked = async (rowId: number, checked: boolean) => {
    // Optimistic update
    setItems((prev) =>
      prev.map((it) => (it.id === rowId ? { ...it, checked } : it)),
    );
    try {
      const res = await fetch(`${getBaseUrl()}/api/shopping-list/${rowId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checked }),
      });
      if (!res.ok) throw new Error();
    } catch {
      // Revert
      setItems((prev) =>
        prev.map((it) =>
          it.id === rowId ? { ...it, checked: !checked } : it,
        ),
      );
      toast({
        title: "Couldn't update item",
        variant: "destructive",
      });
    }
  };

  const setLineChecked = async (rowIds: number[], checked: boolean) => {
    await Promise.all(rowIds.map((id) => setRowChecked(id, checked)));
  };

  const removeRow = async (rowId: number) => {
    const prev = items;
    setItems((p) => p.filter((it) => it.id !== rowId));
    try {
      const res = await fetch(`${getBaseUrl()}/api/shopping-list/${rowId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
    } catch {
      setItems(prev);
      toast({ title: "Couldn't remove item", variant: "destructive" });
    }
  };

  const removeLine = async (rowIds: number[]) => {
    await Promise.all(rowIds.map((id) => removeRow(id)));
  };

  const clearChecked = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${getBaseUrl()}/api/shopping-list?scope=checked`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error();
      await refresh();
      toast({ title: "Checked items cleared" });
    } catch {
      toast({ title: "Couldn't clear items", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    if (!confirm("Clear the entire shopping list?")) return;
    setBusy(true);
    try {
      const res = await fetch(`${getBaseUrl()}/api/shopping-list`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error();
      await refresh();
      toast({ title: "Shopping list cleared" });
    } catch {
      toast({ title: "Couldn't clear list", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // Group items by category, then combine within each category.
  const grouped = useMemo(() => {
    const byCat = new Map<string, ShoppingItem[]>();
    for (const it of items) {
      const cat = it.category || "other";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(it);
    }
    return INGREDIENT_CATEGORY_ORDER.map((cat) => {
      const subset = byCat.get(cat) ?? [];
      return {
        category: cat as IngredientCategory,
        label: INGREDIENT_CATEGORY_LABELS[cat as IngredientCategory],
        lines: combineItems(subset),
      };
    }).filter((g) => g.lines.length > 0);
  }, [items]);

  // Plain-text rendering for copy & print.
  const plainText = useMemo(() => {
    const parts: string[] = ["Shopping List", ""];
    for (const grp of grouped) {
      parts.push(grp.label.toUpperCase());
      for (const line of grp.lines) {
        if (line.combinedAmount !== null && line.parts.length === 1) {
          const amt = [line.combinedAmount, line.combinedUnit ?? ""]
            .filter(Boolean)
            .join(" ");
          parts.push(`  ${line.allChecked ? "[x]" : "[ ]"} ${amt} ${line.item}`.replace(/\s+/g, " ").trim());
        } else {
          parts.push(`  ${line.allChecked ? "[x]" : "[ ]"} ${line.item}`);
          for (const p of line.parts) {
            const amt = [p.amount ?? "", p.unit ?? ""].filter(Boolean).join(" ").trim();
            const recipe = p.recipeTitle ? ` — ${p.recipeTitle}` : "";
            parts.push(`      ${p.checked ? "[x]" : "[ ]"} ${amt}${recipe}`.replace(/\s+/g, " ").trimEnd());
          }
        }
      }
      parts.push("");
    }
    return parts.join("\n");
  }, [grouped]);

  const copyAsText = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Your browser blocked clipboard access.",
        variant: "destructive",
      });
    }
  };

  const printList = () => window.print();

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading shopping list…
        </div>
      </Layout>
    );
  }

  const totalLines = grouped.reduce((acc, g) => acc + g.lines.length, 0);
  const checkedLines = grouped.reduce(
    (acc, g) => acc + g.lines.filter((l) => l.allChecked).length,
    0,
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 print:py-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Cookbook
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <ShoppingCart className="w-7 h-7 text-primary" />
          <h1
            className="text-3xl font-bold text-foreground"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Shopping List
          </h1>
        </div>
        {totalLines > 0 ? (
          <p className="text-muted-foreground text-sm mb-6 print:mb-2">
            {checkedLines} of {totalLines} items checked off
          </p>
        ) : (
          <p className="text-muted-foreground text-sm mb-6">
            Your list is empty. Open a recipe and tap "Add to shopping list".
          </p>
        )}

        {totalLines > 0 && (
          <div className="flex flex-wrap gap-2 mb-6 print:hidden">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyAsText}
              className="gap-1.5"
            >
              <Copy className="w-4 h-4" />
              Copy as text
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={printList}
              className="gap-1.5"
            >
              <Printer className="w-4 h-4" />
              Print
            </Button>
            {checkedLines > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={clearChecked}
                disabled={busy}
                className="gap-1.5"
              >
                <Check className="w-4 h-4" />
                Clear checked ({checkedLines})
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={clearAll}
              disabled={busy}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" />
              Clear all
            </Button>
          </div>
        )}

        {/* Grouped lists */}
        <div className="space-y-8 print:space-y-4">
          {grouped.map((grp) => (
            <section key={grp.category}>
              <h2
                className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 border-b border-border/60 pb-1"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {grp.label}
              </h2>
              <ul className="space-y-1">
                {grp.lines.map((line) => {
                  const isSimple =
                    line.combinedAmount !== null && line.parts.length === 1;
                  return (
                    <li key={line.key} className="group">
                      <div className="flex items-start gap-3 py-1.5">
                        <input
                          type="checkbox"
                          className="mt-1.5 w-4 h-4 rounded border-border accent-primary cursor-pointer flex-shrink-0"
                          checked={line.allChecked}
                          onChange={(e) =>
                            setLineChecked(line.rowIds, e.target.checked)
                          }
                          aria-label={`Check off ${line.item}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              "text-sm sm:text-base text-foreground",
                              line.allChecked &&
                                "line-through text-muted-foreground",
                            )}
                          >
                            {isSimple && (
                              <>
                                {line.combinedAmount && (
                                  <span className="font-semibold">
                                    {line.combinedAmount}
                                  </span>
                                )}
                                {line.combinedUnit && (
                                  <span className="font-medium ml-1">
                                    {line.combinedUnit}
                                  </span>
                                )}
                                <span className="ml-1">{line.item}</span>
                              </>
                            )}
                            {!isSimple && (
                              <span className="font-medium">{line.item}</span>
                            )}
                          </div>

                          {!isSimple && line.parts.length > 0 && (
                            <ul className="mt-1 ml-1 space-y-0.5 border-l-2 border-border/40 pl-3">
                              {line.parts.map((p) => {
                                const amt = [p.amount ?? "", p.unit ?? ""]
                                  .filter(Boolean)
                                  .join(" ")
                                  .trim();
                                return (
                                  <li
                                    key={p.rowId}
                                    className="flex items-start gap-2 text-xs sm:text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-1 w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer flex-shrink-0"
                                      checked={p.checked}
                                      onChange={(e) =>
                                        setRowChecked(p.rowId, e.target.checked)
                                      }
                                      aria-label={`Check off ${amt} ${line.item}`}
                                    />
                                    <span
                                      className={cn(
                                        "flex-1",
                                        p.checked &&
                                          "line-through text-muted-foreground",
                                      )}
                                    >
                                      {amt && (
                                        <span className="font-medium text-foreground">
                                          {amt}
                                        </span>
                                      )}
                                      {p.prepNotes && (
                                        <span className="text-muted-foreground ml-1">
                                          ({p.prepNotes})
                                        </span>
                                      )}
                                      {p.recipeTitle && (
                                        <span className="text-muted-foreground ml-2 italic">
                                          for {p.recipeTitle}
                                          {p.multiplier !== "1" &&
                                            ` ×${p.multiplier}`}
                                        </span>
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeRow(p.rowId)}
                                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1 print:hidden"
                                      aria-label="Remove this item"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                        {isSimple && (
                          <button
                            type="button"
                            onClick={() => removeLine(line.rowIds)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1 flex-shrink-0 print:hidden"
                            aria-label="Remove this item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        {totalLines === 0 && (
          <div className="border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground">
            <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">
              Open any recipe and tap{" "}
              <span className="font-medium text-foreground">
                "Add to shopping list"
              </span>{" "}
              to start building your list.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
